"use client"
import React, { useState } from 'react'
import styles from './contact.module.scss'
import Icon from '@components/icon'
import { CATEGORIES, TYPES } from '@lib/helpers'
import { ICategory, IType } from '@models'
import {
    faAt, faComment, faGlobe, faImage, faLink, faPhone, faSignal,
    faTag, faTowerBroadcast, faUser
} from '@fortawesome/free-solid-svg-icons'
import { IconProp } from '@fortawesome/fontawesome-svg-core'

type IStatus = 'success' | 'error' | 'loading' | 'none'
type IMode = 'contact' | 'station'

const EMPTY_CONTACT = { name: '', email: '', phone: '', message: '' }
const EMPTY_STATION = {
    stationName: '', streamUrl: '', logoUrl: '', website: '', freq: '',
    type: 'internet' as IType, categories: [] as ICategory[], description: '',
    name: '', email: '', phone: '', notes: ''
}
// The station form asks for the fields a station page is actually built from,
// in the order they are read on it. `favorites` and `top` are computed from
// usage rather than declared, so they are not offered here.
const CATEGORY_KEYS = (Object.keys(CATEGORIES) as ICategory[]).filter(c => !['favorites', 'top'].includes(c))
const TYPE_KEYS = Object.keys(TYPES) as IType[]

const MODES: { key: IMode, title: string, icon: IconProp }[] = [
    { key: 'contact', title: 'יצירת קשר', icon: faComment },
    { key: 'station', title: 'הוספת תחנה', icon: faTowerBroadcast },
]

interface IField {
    label: string
    icon: IconProp
    required?: boolean
    wide?: boolean
    hint?: string
    children: React.ReactNode
}
// A label wrapping its control: the whole row is a hit target, and the icon
// sits inside the field rather than beside it, so a full-width textarea and a
// half-width input still line up on the same grid.
const Field = ({ label, icon, required, wide, hint, children }: IField) => (
    <label className={`${styles.field} ${wide ? styles.wide : ''}`}>
        <span className={styles.label}>
            {label}
            {required && <b> *</b>}
        </span>
        <div className={styles.control}>
            {children}
            <Icon icon={icon} />
        </div>
        {hint && <small>{hint}</small>}
    </label>
)

export default function Contact() {
    const [mode, setMode] = useState<IMode>('contact')
    const [contact, setContact] = useState(EMPTY_CONTACT)
    const [station, setStation] = useState(EMPTY_STATION)
    const [status, setStatus] = useState<IStatus>('none')

    const changeMode = (next: IMode) => {
        setMode(next)
        // The banner belongs to the form that produced it - keeping a success
        // message above a different, empty form reads as if it was already sent.
        setStatus('none')
    }
    const handleContact = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        setContact({ ...contact, [name]: value })
    }
    const handleStation = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setStation({ ...station, [name]: value })
    }
    const toggleCategory = (category: ICategory) => {
        setStation({
            ...station,
            categories: station.categories.includes(category)
                ? station.categories.filter(c => c !== category)
                : [...station.categories, category]
        })
    }
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (status === 'loading') return
        setStatus('loading')
        try {
            const response = await fetch('/api/contact/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                // `form`, not `type`: the station payload carries a `type` of its
                // own (national/regional/...), and spreading it over the mode
                // would leave the route reading a station as a plain message.
                body: JSON.stringify({ form: mode, ...(mode === 'contact' ? contact : station) })
            })
            if (!response.ok)
                throw new Error('request failed')
            if (mode === 'contact')
                setContact(EMPTY_CONTACT)
            else
                setStation(EMPTY_STATION)
            setStatus('success')
        } catch (error) {
            setStatus('error')
            console.error(error)
        }
    }

    return (
        <div className={styles.container}>
            <div className={styles.intro}>
                <h1>יצירת קשר עם מערכת {process.env.NEXT_PUBLIC_SITE_NAME}</h1>
                {
                    mode === 'contact' ? (
                        <p>
                            רוצים לדווח על תקלה באחת התחנות, להציע ייעול, או לדבר איתנו על שיתוף פעולה ופרסום באתר?
                            שלחו הודעה - אנחנו קוראים כל פנייה ומשיבים בהקדם.
                        </p>
                    ) : (
                        <p>
                            מפעילים תחנת רדיו ורוצים שתופיע ב{process.env.NEXT_PUBLIC_SITE_NAME}?
                            מלאו את פרטי התחנה, נבדוק את הסטרים ונוסיף אותה לאתר - ללא תשלום.
                        </p>
                    )
                }
            </div>

            <div className={styles.modes} role='group' aria-label='סוג הפנייה'>
                {
                    MODES.map(({ key, title, icon }) => (
                        <button
                            key={key}
                            type='button'
                            aria-pressed={mode === key}
                            className={`click animated ${mode === key ? styles.active : ''}`}
                            onClick={() => changeMode(key)}
                        >
                            <Icon icon={icon} />
                            {title}
                        </button>
                    ))
                }
            </div>

            {
                status === 'success' && (
                    <span className={`${styles.banner} ${styles.success}`}>
                        {
                            mode === 'contact'
                                ? 'ההודעה נשלחה בהצלחה, נחזור אליכם בהקדם'
                                : 'בקשת ההוספה נשלחה, נבדוק את התחנה ונחזור אליכם'
                        }
                    </span>
                )
            }
            {
                status === 'error' && (
                    <span className={`${styles.banner} ${styles.error}`}>אירעה שגיאה בשליחה, אנא נסו שנית</span>
                )
            }

            <form onSubmit={handleSubmit} lang='he' className={styles.form}>
                {
                    mode === 'contact' ? (
                        <div className={styles.grid}>
                            <Field label='שם מלא' icon={faUser} required>
                                <input type='text' name='name' value={contact.name} onChange={handleContact} placeholder='ישראל ישראלי' required />
                            </Field>
                            <Field label='דוא״ל' icon={faAt} required>
                                <input type='email' name='email' value={contact.email} onChange={handleContact} placeholder='name@example.com' required />
                            </Field>
                            <Field label='טלפון' icon={faPhone} wide>
                                <input type='tel' name='phone' value={contact.phone} onChange={handleContact} placeholder='050-0000000' />
                            </Field>
                            <Field label='במה נוכל לעזור?' icon={faComment} required wide>
                                <textarea name='message' value={contact.message} onChange={handleContact} placeholder='כתבו לנו כאן...' rows={6} required />
                            </Field>
                        </div>
                    ) : (
                        <>
                            <div className={styles.grid}>
                                <h2 className={styles.legend}>פרטי התחנה</h2>
                                <Field label='שם התחנה' icon={faTowerBroadcast} required>
                                    <input type='text' name='stationName' value={station.stationName} onChange={handleStation} placeholder='רדיו הדוגמה' required />
                                </Field>
                                <Field label='תדר' icon={faSignal} hint='ריק אם התחנה משדרת באינטרנט בלבד'>
                                    <input type='text' name='freq' value={station.freq} onChange={handleStation} placeholder='91.8FM' />
                                </Field>
                                <Field label='כתובת הסטרים' icon={faLink} required wide hint='קישור ישיר לשידור החי (mp3/aac/m3u8)'>
                                    <input type='url' name='streamUrl' value={station.streamUrl} onChange={handleStation} placeholder='https://stream.example.com/live' required />
                                </Field>
                                <Field label='קישור ללוגו' icon={faImage} required hint='רצוי PNG או WEBP ברקע שקוף'>
                                    <input type='url' name='logoUrl' value={station.logoUrl} onChange={handleStation} placeholder='https://example.com/logo.png' required />
                                </Field>
                                <Field label='אתר התחנה' icon={faGlobe}>
                                    <input type='url' name='website' value={station.website} onChange={handleStation} placeholder='https://example.com' />
                                </Field>
                                <Field label='סוג התחנה' icon={faTag} required>
                                    <select name='type' value={station.type} onChange={handleStation} required>
                                        {
                                            TYPE_KEYS.map(type => (
                                                <option key={type} value={type}>{TYPES[type].name}</option>
                                            ))
                                        }
                                    </select>
                                </Field>
                                <Field label='תיאור התחנה' icon={faComment} required wide hint='כמה משפטים על סגנון השידור, הקהל והתוכניות'>
                                    <textarea name='description' value={station.description} onChange={handleStation} placeholder='תחנה המשדרת...' rows={5} required />
                                </Field>
                                <div className={`${styles.field} ${styles.wide}`}>
                                    <span className={styles.label}>קטגוריות (ניתן לסמן כמה)</span>
                                    <div className={styles.categories}>
                                        {
                                            CATEGORY_KEYS.map(category => (
                                                <label
                                                    key={category}
                                                    className={`click animated ${station.categories.includes(category) ? styles.checked : ''}`}
                                                >
                                                    <input
                                                        type='checkbox'
                                                        checked={station.categories.includes(category)}
                                                        onChange={() => toggleCategory(category)}
                                                    />
                                                    <Icon icon={CATEGORIES[category].icon} />
                                                    {CATEGORIES[category].name}
                                                </label>
                                            ))
                                        }
                                    </div>
                                </div>
                            </div>
                            <div className={styles.grid}>
                                <h2 className={styles.legend}>פרטי איש הקשר</h2>
                                <Field label='שם מלא' icon={faUser} required>
                                    <input type='text' name='name' value={station.name} onChange={handleStation} placeholder='ישראל ישראלי' required />
                                </Field>
                                <Field label='דוא״ל' icon={faAt} required>
                                    <input type='email' name='email' value={station.email} onChange={handleStation} placeholder='name@example.com' required />
                                </Field>
                                <Field label='טלפון' icon={faPhone} wide>
                                    <input type='tel' name='phone' value={station.phone} onChange={handleStation} placeholder='050-0000000' />
                                </Field>
                                <Field label='הערות' icon={faComment} wide>
                                    <textarea name='notes' value={station.notes} onChange={handleStation} placeholder='כל מה שחשוב שנדע' rows={4} />
                                </Field>
                            </div>
                        </>
                    )
                }
                <button type='submit' className={`click animated ${styles.submit}`} disabled={status === 'loading'}>
                    {
                        status === 'loading'
                            ? 'בדרך...'
                            : mode === 'contact' ? 'שליחה' : 'שליחת הבקשה'
                    }
                </button>
                <small className={styles.note}>שדות המסומנים ב-* הם שדות חובה.</small>
            </form>
        </div>
    )
}
